import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { IApplyCourse, StudyLevel, FeeStatus } from 'api-paths-cas';
import { ApplicationState } from '@/store';
import { getAllCoursesForUniversity, getApplicantIntakes } from '@/store/apply/actions';
import {
  getAggregatedCoursesForSelection,
  getAvailableStudyLevelsForIntake,
  getModesForCourseName,
  getCampusesForCriteria,
  getEntryPointsForCriteria,
  findCourseIdForCriteria,
} from '../utils/courseFiltering';

export const useCourseData = (
  _watchedInstitution: string, // Prefix with _ to indicate intentionally unused
  watchedIntake: string,
  watchedFeeStatus: string,
  watchedStudyLevel: string,
  courseName: string,
  courseMode: string,
  courseCampus: string,
  courseEntryPoint: string,
) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  // Get URL parameters
  const intakeFromUrl = searchParams.get('intakeId');

  // Get Redux state
  const storeIntake = useSelector(({ auth }: ApplicationState) => auth.intake);

  const loadingCourses = useSelector(({ apply }: ApplicationState) => apply.loadingCourses || false);
  const allCourses = useSelector(({ apply }: ApplicationState) => (apply.courses || []) as IApplyCourse[]);
  const applicationIntakes = useSelector(({ apply }: ApplicationState) => apply.applicantIntakes || []);
  const loadingApplicationIntakes = useSelector(
    ({ apply }: ApplicationState) => apply.loadingApplicantIntakes || false,
  );

  // Fetch intakes for university (filtered by fee status on the backend)
  const fetchIntakesForUniversity = useCallback(
    (universityId: string, feeStatus?: string) => {
      if (!universityId) return;
      dispatch(getApplicantIntakes(universityId, feeStatus));
    },
    [dispatch],
  );

  // Fetch courses for a specific intake
  const fetchCoursesForIntake = useCallback(
    (uni: string, intakeId: string, feeStatus?: string) => {
      if (!uni || !intakeId) return;
      dispatch(getAllCoursesForUniversity(uni, intakeId, feeStatus));
    },
    [dispatch],
  );

  // Intakes are now filtered by fee status on the backend
  const intakeOptions = applicationIntakes;

  // Derive study levels from the intake object when available (set by the backend for non-partner users),
  // so the study level dropdown is usable immediately after intake selection without waiting for courses.
  // Falls back to deriving from courses for partners, where the backend returns an empty studyLevels array.
  const filteredStudyLevelOptions = useMemo(() => {
    if (!watchedIntake) return [];
    const selectedIntake = applicationIntakes.find(i => i.value === watchedIntake);
    if (selectedIntake?.studyLevels?.length) {
      return selectedIntake.studyLevels
        .map(level => ({ label: level, value: level }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    // Fallback for partners: derive from courses once loaded
    if (!watchedFeeStatus || allCourses.length === 0) return [];
    return getAvailableStudyLevelsForIntake(allCourses, watchedIntake, watchedFeeStatus as FeeStatus);
  }, [applicationIntakes, watchedIntake, watchedFeeStatus, allCourses]);

  // Get available courses (aggregated by name) for the selected intake and study level
  const availableCourses = useMemo(() => {
    if (!watchedIntake || !watchedFeeStatus || !watchedStudyLevel || allCourses.length === 0) {
      return [];
    }
    return getAggregatedCoursesForSelection(
      allCourses,
      watchedIntake,
      watchedFeeStatus as FeeStatus,
      watchedStudyLevel as StudyLevel,
    );
  }, [allCourses, watchedIntake, watchedFeeStatus, watchedStudyLevel]);

  // Get available modes for the selected course name
  const availableModes = useMemo(() => {
    if (!watchedIntake || !watchedFeeStatus || !watchedStudyLevel || !courseName || allCourses.length === 0) {
      return [];
    }
    return getModesForCourseName(
      allCourses,
      watchedIntake,
      watchedFeeStatus as FeeStatus,
      watchedStudyLevel as StudyLevel,
      courseName,
    );
  }, [allCourses, watchedIntake, watchedFeeStatus, watchedStudyLevel, courseName]);

  // Get available campuses for the selected course name and mode
  const availableCampuses = useMemo(() => {
    if (
      !watchedIntake ||
      !watchedFeeStatus ||
      !watchedStudyLevel ||
      !courseName ||
      !courseMode ||
      allCourses.length === 0
    ) {
      return [];
    }
    return getCampusesForCriteria(
      allCourses,
      watchedIntake,
      watchedFeeStatus as FeeStatus,
      watchedStudyLevel as StudyLevel,
      courseName,
      courseMode,
    );
  }, [allCourses, watchedIntake, watchedFeeStatus, watchedStudyLevel, courseName, courseMode]);

  // Get available entry years for the selected course name, mode, and campus
  const availableEntryPoints = useMemo(() => {
    if (
      !watchedIntake ||
      !watchedFeeStatus ||
      !watchedStudyLevel ||
      !courseName ||
      !courseMode ||
      !courseCampus ||
      allCourses.length === 0
    ) {
      return [];
    }
    return getEntryPointsForCriteria(
      allCourses,
      watchedIntake,
      watchedFeeStatus as FeeStatus,
      watchedStudyLevel as StudyLevel,
      courseName,
      courseMode,
      courseCampus,
    );
  }, [allCourses, watchedIntake, watchedFeeStatus, watchedStudyLevel, courseName, courseMode, courseCampus]);

  // Find the course ID for the selected criteria
  const resolvedCourseId = useMemo((): string | null => {
    if (
      !watchedIntake ||
      !watchedFeeStatus ||
      !watchedStudyLevel ||
      !courseName ||
      !courseMode ||
      !courseCampus ||
      !courseEntryPoint ||
      allCourses.length === 0
    ) {
      return null;
    }
    return findCourseIdForCriteria(
      allCourses,
      watchedIntake,
      watchedFeeStatus as FeeStatus,
      watchedStudyLevel as StudyLevel,
      courseName,
      courseMode,
      courseCampus,
      courseEntryPoint,
    );
  }, [
    allCourses,
    watchedIntake,
    watchedFeeStatus,
    watchedStudyLevel,
    courseName,
    courseMode,
    courseCampus,
    courseEntryPoint,
  ]);

  // Get current selections from URL or store
  const currentIntake = intakeFromUrl || storeIntake;

  return {
    currentIntake,
    intakeOptions,
    filteredStudyLevelOptions,
    availableCourses,
    availableModes,
    availableCampuses,
    availableEntryPoints,
    resolvedCourseId,
    allCourses,
    loadingCourses,
    loadingApplicationIntakes,
    fetchIntakesForUniversity,
    fetchCoursesForIntake,
  };
};
